// src/pages/ResetPassword.tsx

import React, { useEffect, useState } from "react";
import { NotificationType, useNotification } from "../context/NotificationContext.tsx";
import { useAuthApi } from "../context/ApiContext.tsx";
import houseImage from "../assets/house.jpg";
import stewartLogo from "../assets/stewart_logo.png";

// the backend sends nothing new within 2 minutes anyway; this keeps a double press from looking like a failure
const RESEND_WAIT_SECONDS = 30;

export const ResetPassword = () => {
    const [username, setUsername] = useState('');
    const authApi = useAuthApi();
    const { showNotification } = useNotification();
    const [isLoading, setIsLoading] = useState(false);
    const [confirmation, setConfirmation] = useState('');
    const [waitSeconds, setWaitSeconds] = useState(0);

    useEffect(() => {
        if (waitSeconds <= 0) {
            return;
        }
        const timer = setTimeout(() => setWaitSeconds(waitSeconds - 1), 1000);
        return () => clearTimeout(timer);
    }, [waitSeconds]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setIsLoading(true);
        authApi.sendResetPasswordEmail({ username: username.trim() })
            .then((message) => {
                setConfirmation(message);
                setWaitSeconds(RESEND_WAIT_SECONDS);
            })
            .catch((error) => {
                console.log("Password Reset failed: ", error);
                showNotification(NotificationType.ERROR, 'We could not send the reset link. Try again in a few minutes.');
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-start bg-cover bg-center bg-no-repeat"
             style={{ backgroundImage: `url(${houseImage})` }}
        >
            {/* Content container */}
            <div className="relative z-10 w-full max-w-md px-4 sm:px-6 min-h-screen py-4 sm:py-6 flex flex-col justify-between">
                {/* Logo section - improved spacing */}
                <div className="text-center pt-4 sm:pt-6">
                    <div className="flex justify-center mb-4 sm:mb-8">
                        <img
                            src={stewartLogo}
                            alt="Stewart Logo"
                            className="h-14 sm:h-16 w-auto object-contain"
                        />
                    </div>
                </div>

                {/* Reset Password Card */}
                <div className="bg-white/95 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-lg p-5 sm:p-8 mx-auto w-full">
                    <div className="mb-4 sm:mb-6 text-center">
                        <h1 className="text-xl sm:text-2xl font-bold text-blue-800">Reset Password</h1>
                        <p className="text-sm text-gray-600 mt-1">Enter your username or email address to reset your password</p>
                    </div>

                    {confirmation && (
                        <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200 text-sm text-green-800">
                            {confirmation}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                        <div>
                            <label htmlFor="username" className="block mb-1.5 text-sm font-medium text-gray-700">
                                Username or email
                            </label>
                            <input
                                id="username"
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md
                                         focus:outline-none focus:ring-2 focus:ring-blue-500
                                         focus:border-blue-500 text-sm"
                                placeholder="Enter your username or email"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading || waitSeconds > 0}
                            className="w-full py-2.5 bg-blue-600 text-white text-sm font-medium
                                     rounded-md hover:bg-blue-700 focus:outline-none
                                     focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                                     disabled:opacity-50 disabled:cursor-not-allowed
                                     flex items-center justify-center transition-colors"
                        >
                            {isLoading ? (
                                <>
                                    <div className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Sending link...
                                </>
                            ) : waitSeconds > 0 ? (
                                `Send again in ${waitSeconds}s`
                            ) : (
                                'Send Reset Link'
                            )}
                        </button>

                        <div className="mt-4 sm:mt-6">
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-200" />
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-4 text-gray-500 bg-white">
                                        Need help?
                                    </span>
                                </div>
                            </div>

                            {/* help links */}
                            <div className="mt-4 flex justify-between px-2">
                                <a
                                    href="https://www.unitedstatesappraisals.com/join-our-team/"
                                    target="_blank"
                                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                                >
                                    Sign Up
                                </a>
                                <a
                                    href="/login"
                                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                                >
                                    Back to Login
                                </a>
                            </div>
                        </div>
                    </form>
                </div>

                {/* Footer with some spacing at the bottom */}
                <div className="py-4 text-center text-white/90 text-sm">
                    © {new Date().getFullYear()} Stewart Valuation Intelligence
                </div>
            </div>
        </div>
    );
};